import { useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { chartModel, money, monthLabel, tickDate, utcDate, type CategoryRow, type StatsCard } from './statementsData'

const W = 600
const H = 150

interface Props {
  cards: StatsCard[]
  currency: string
  locale: string
  open: ReadonlySet<string>
  onToggle: (id: string) => void
}

/** Earning statistics: "All time" and one collapsible card per month (OnlyFans /my/statements/stats). */
export function StatsCards({ cards, currency, locale, open, onToggle }: Props) {
  const { t } = useTranslation('ofStatements')
  const label = (r: CategoryRow) => (r.category ? t(`stats.category.${r.category}`) : r.label)
  return (
    <>
      {cards.map((card) => {
        const isOpen = open.has(card.id)
        const title = card.month ? monthLabel(card.month, locale) : t('stats.allTime')
        return (
          <section key={card.id} className={`ofs-card ofs-stats-card ${isOpen ? 'is-open' : ''}`} aria-label={title}>
            <button type="button" className="ofs-stats-head" aria-expanded={isOpen} onClick={() => onToggle(card.id)}>
              <span>{title}</span>
              <strong>{money(card.net, currency)}</strong>
              <FeatherIcon name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>
            {isOpen && (
              <div className="ofs-stats-body">
                <Chart rows={card.rows} currency={currency} locale={locale} label={label} />
                {card.range && (
                  <div className="ofs-stats-range">
                    <FeatherIcon name="calendar" size={18} />
                    <span>
                      {t('stats.from')} <strong>{utcDate(card.range.from, locale)}</strong> {t('stats.to')}{' '}
                      <strong>{utcDate(card.range.to, locale)}</strong>
                    </span>
                  </div>
                )}
                <div className="ofs-table-scroll">
                <table className="ofs-table ofs-table--stats">
                  <thead>
                    <tr>
                      <th>{t('col.category')}</th>
                      <th className="ofs-num">{t('col.gross')}</th>
                      <th className="ofs-num">{t('col.net')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {card.rows.map((r) => (
                      <tr key={r.category ?? r.label}>
                        <td>
                          <span className={`ofs-dot ofs-tone-${r.tone}`} aria-hidden="true" />
                          {label(r)}
                        </td>
                        <td className="ofs-num">{money(r.gross, currency)}</td>
                        <td className="ofs-num">{money(r.net, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>{t('col.total')}</td>
                      <td className="ofs-num">
                        <small>{t('col.gross')}</small> {money(card.gross, currency)}
                      </td>
                      <td className="ofs-num">
                        <small>{t('col.net')}</small> {money(card.net, currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            )}
          </section>
        )
      })}
    </>
  )
}

/** Cumulative net per category over time, with a crosshair and tooltip under the pointer. */
function Chart({ rows, currency, locale, label }: { rows: CategoryRow[]; currency: string; locale: string; label: (r: CategoryRow) => string }) {
  const { t } = useTranslation('ofStatements')
  const model = useMemo(() => chartModel(rows), [rows])
  const [hover, setHover] = useState<{ i: number; left: number; width: number } | null>(null)
  const tip = useRef<HTMLDivElement | null>(null)

  // Keep the tooltip inside the plot once its width is known.
  useLayoutEffect(() => {
    const el = tip.current
    if (!el || !hover) return
    const w = el.offsetWidth
    el.style.left = `${Math.min(Math.max(hover.left - w / 2, 0), Math.max(0, hover.width - w))}px`
  }, [hover])

  if (!model) return <div className="ofs-chart-empty">{t('empty.chart')}</div>
  const { times, series, cumulative, max } = model
  const x = (i: number) => (times.length === 1 ? W / 2 : (i / (times.length - 1)) * W)
  const y = (v: number) => H - 6 - (v / max) * (H - 16)

  const move = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    if (!rect.width) return
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    const i = Math.round(frac * (times.length - 1))
    setHover({ i, left: (x(i) / W) * rect.width, width: rect.width })
  }

  const tickCount = Math.min(5, times.length)
  const ticks = Array.from({ length: tickCount }, (_, k) => {
    const i = tickCount === 1 ? 0 : Math.round((k / (tickCount - 1)) * (times.length - 1))
    const align = k === 0 ? 'start' : k === tickCount - 1 ? 'end' : 'middle'
    return { i, align, left: (x(i) / W) * 100 }
  })

  return (
    <div className="ofs-chart">
      <div className="ofs-chart-plot" onPointerMove={move} onPointerLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t('stats.chart')}>
          <g className="ofs-grid">
            {[0.25, 0.5, 0.75].map((f) => (
              <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} vectorEffect="non-scaling-stroke" />
            ))}
          </g>
          {series.map((s, si) => (
            <path
              key={s.category ?? s.label}
              className={`ofs-line ofs-tone-${s.tone}`}
              d={cumulative[si]!.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('')}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        {hover && (
          <>
            <div className="ofs-crosshair" style={{ left: hover.left }} />
            <div className="ofs-tooltip" ref={tip}>
              <div className="ofs-tooltip-date">{utcDate(times[hover.i]! * 1000, locale)}</div>
              {series.map((s, si) => (
                <div key={s.category ?? s.label}>
                  <span className={`ofs-dot ofs-tone-${s.tone}`} aria-hidden="true" />
                  {label(s)}
                  <strong>{money(cumulative[si]![hover.i], currency)}</strong>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="ofs-ticks">
        {ticks.map((tk) => (
          <span key={`${tk.i}-${tk.align}`} className={`is-${tk.align}`} style={{ left: `${tk.left.toFixed(2)}%` }}>
            {tickDate(times[tk.i]!, locale)}
          </span>
        ))}
      </div>
    </div>
  )
}
